import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const COMMANDS = {
  help: 'Available commands: help, about, skills, projects, contact, experience, education, tech, github, linkedin, clear, matrix, joke, quote, hack, coffee, weather, time, whoami, sudo, rm, cat, ls, pwd, ping, fortune, cowsay',
  about: 'Full-stack developer with a passion for creating innovative web solutions. Specialized in JavaScript, React, Node.js, and modern web technologies.',
  skills: 'Frontend: React, Next.js, Vue.js, Tailwind CSS, TypeScript\nBackend: Node.js, Python, PHP, Laravel\nDatabase: PostgreSQL, MongoDB, MySQL\nDevOps: AWS, Docker, Kubernetes, GitHub Actions',
  projects: 'Featured Projects:\n1. Quizzle - AI-powered adaptive learning platform (96.15% satisfaction)\n2. Drafter Autotech Inventory System - Improved efficiency by 20%\n3. The Mannings Realty Agent Portal - Reduced onboarding time by 40%',
  contact: 'Email: jadedelara25@gmail.com\nPhone: 09668559513\nLocation: Metro Manila, Philippines',
  experience: 'Recent Experience:\n- Accenture SAP ABAP Academy Delegate (2026)\n- System Analyst at Drafter Autotech (2025)\n- Lead Developer for Quizzle Capstone (2024)',
  education: 'BS Information Technology\nUniversity of Santo Tomas (2022-2026)\nManila, Philippines',
  tech: 'Current Tech Stack: JavaScript, TypeScript, React, Node.js, Python, PHP, Laravel, PostgreSQL, MongoDB, AWS, Docker',
  github: 'GitHub: github.com/jadestack',
  linkedin: 'LinkedIn: linkedin.com/in/jadelloyddelara',
  clear: 'clear',
  matrix: 'matrix',
  whoami: 'jade@developer ~ A passionate full-stack developer who loves building innovative solutions',
  pwd: '/home/jade/portfolio/projects',
  ls: 'projects/  skills/  experience/  contact/  README.md',
  cat: 'Usage: cat [filename]\nTry: cat README.md',
  'cat readme.md': 'JADE DE LARA - PORTFOLIO\n=======================\nFull-stack developer specializing in modern web technologies.\nBuilding the future, one line of code at a time.',
  ping: 'Pinging portfolio server... Response time: 1ms - Connection excellent!',
  time: () => `Current time: ${new Date().toLocaleTimeString()} - Keep coding!`,
  weather: 'Weather in Manila: Sunny with a chance of debugging sessions',
  coffee: 'Brewing coffee... ☕\n[████████████████████] 100%\nCoffee ready! Time to code!',
  sudo: 'Nice try! You already have all the permissions you need here.',
  'sudo rm': 'Permission denied. I cannot let you do that, Dave.',
  rm: 'rm: cannot remove: This portfolio is protected by awesome code!',
  hack: 'Initiating hack sequence...\n[▓▓▓▓▓▓▓▓▓▓] 100%\nACCESS GRANTED\nJust kidding! But nice try.',
  fortune: [
    'You will write bug-free code today... probably.',
    'A great opportunity awaits you in the cloud... AWS is calling!',
    'Your next commit will be legendary.',
    'The code you write today will inspire developers tomorrow.',
    'Stack Overflow will have the answer you seek.',
    'Today is a good day to refactor legacy code.',
    'Your pull request will be approved without comments.',
  ],
  cowsay: 'Usage: cowsay [message]\nTry: cowsay hello',
  'cowsay hello': ' _______\n< hello >\n -------\n        \\   ^__^\n         \\  (oo)\\_______\n            (__)\\       )\\/\\\n                ||----w |\n                ||     ||',
  quote: [
    '"Code is like humor. When you have to explain it, it\'s bad." - Cory House',
    '"First, solve the problem. Then, write the code." - John Johnson',
    '"Experience is the name everyone gives to their mistakes." - Oscar Wilde',
    '"In order to be irreplaceable, one must always be different." - Coco Chanel',
    '"Java is to JavaScript what car is to carpet." - Chris Heilmann',
    '"The best error message is the one that never shows up." - Thomas Fuchs',
    '"Simplicity is the soul of efficiency." - Austin Freeman',
    '"Make it work, make it right, make it fast." - Kent Beck',
  ],
  joke: [
    "Why do programmers prefer dark mode? Because light attracts bugs!",
    "How many programmers does it take to change a light bulb? None, it's a hardware problem.",
    "Why do Java developers wear glasses? Because they don't C#!",
    "A SQL query walks into a bar, walks up to two tables and asks... 'Can I join you?'",
    "!false - It's funny because it's true!",
    "Why did the programmer quit his job? Because he didn't get arrays.",
    "How do you comfort a JavaScript bug? You console it.",
    "Why do programmers always mix up Halloween and Christmas? Because Oct 31 == Dec 25!",
    "What's a programmer's favorite hangout spot? The Foo Bar.",
    "Why did the developer go broke? Because he used up all his cache.",
    "What do you call a programmer from Finland? Nerdic.",
    "Why don't backers ever get lonely? They hang out in pairs!",
    "How do functions break up? They stop calling each other.",
    "What's the object-oriented way to become wealthy? Inheritance.",
    "Why did the database administrator leave his wife? She had one-to-many relationships.",
  ],
};

export default function DevConsole({ isOpen, onClose }) {
  const [history, setHistory] = useState([
    { type: 'system', text: 'Welcome to Jade\'s Dev Console! Type "help" for available commands.' },
  ]);
  const [input, setInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const consoleRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [history]);

  const executeCommand = (cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    setHistory(prev => [...prev, { type: 'input', text: `$ ${cmd}` }]);

    if (!trimmed) return;

    setCommandHistory(prev => [...prev, cmd]);
    setHistoryIndex(-1);

    if (trimmed === 'clear') {
      setTimeout(() => setHistory([]), 100);
      return;
    }

    if (trimmed === 'matrix') {
      setHistory(prev => [
        ...prev,
        { type: 'output', text: 'Initiating Matrix mode... Reality is an illusion! Enter the Konami code on the main page!' },
      ]);
      return;
    }

    // Handle cowsay with custom message
    if (trimmed.startsWith('cowsay ')) {
      const message = cmd.slice(7).trim();
      if (message) {
        const cow = ` ${'_'.repeat(message.length + 2)}\n< ${message} >\n ${'‾'.repeat(message.length + 2)}\n        \\   ^__^\n         \\  (oo)\\_______\n            (__)\\       )\\/\\\n                ||----w |\n                ||     ||`;
        setHistory(prev => [...prev, { type: 'output', text: cow }]);
      } else {
        setHistory(prev => [...prev, { type: 'output', text: COMMANDS.cowsay }]);
      }
      return;
    }

    // Handle commands that return random values
    if (trimmed === 'joke' || trimmed === 'fortune' || trimmed === 'quote') {
      const options = COMMANDS[trimmed];
      const randomItem = options[Math.floor(Math.random() * options.length)];
      setHistory(prev => [...prev, { type: 'output', text: randomItem }]);
      return;
    }

    // Handle commands that return functions
    if (trimmed === 'time') {
      const response = COMMANDS[trimmed]();
      setHistory(prev => [...prev, { type: 'output', text: response }]);
      return;
    }

    // Handle sudo rm specially
    if (trimmed === 'sudo rm' || trimmed === 'sudo rm -rf') {
      setHistory(prev => [
        ...prev,
        { type: 'error', text: COMMANDS['sudo rm'] },
      ]);
      return;
    }

    const response = COMMANDS[trimmed];
    if (response) {
      setHistory(prev => [...prev, { type: 'output', text: response }]);
    } else {
      // Check if it's a close match
      const suggestions = Object.keys(COMMANDS).filter(cmd => 
        cmd.includes(trimmed) || trimmed.includes(cmd.split(' ')[0])
      );
      
      let errorMsg = `Command not found: ${trimmed}`;
      if (suggestions.length > 0) {
        errorMsg += `\nDid you mean: ${suggestions.slice(0, 3).join(', ')}?`;
      } else {
        errorMsg += '\nType "help" for available commands.';
      }
      
      setHistory(prev => [
        ...prev,
        { type: 'error', text: errorMsg },
      ]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      executeCommand(input);
      setInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[commandHistory.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="bg-gray-900 p-6 rounded-2xl shadow-2xl border border-jade-500/30 max-w-3xl w-full mx-4"
          style={{ height: '600px' }}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-jade-400 font-mono flex items-center gap-2">
              <span className="w-3 h-3 bg-jade-400 rounded-full animate-pulse"></span>
              jade@portfolio:~$
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div
            ref={consoleRef}
            className="bg-black rounded-lg p-4 font-mono text-sm overflow-y-auto mb-4"
            style={{ height: 'calc(100% - 120px)' }}
          >
            {history.map((item, index) => (
              <div key={index} className={`mb-2 ${
                item.type === 'input' ? 'text-jade-400' :
                item.type === 'error' ? 'text-red-400' :
                item.type === 'system' ? 'text-blue-400' :
                'text-gray-300'
              }`}>
                {item.text}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <span className="text-jade-400 font-mono">$</span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-black/50 text-white p-2 rounded border border-jade-500/30 focus:border-jade-500 focus:outline-none font-mono"
              placeholder="Type a command..."
              autoComplete="off"
            />
          </form>

          <p className="text-center text-xs text-gray-500 mt-2">
            Use ↑/↓ arrows to navigate command history
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
